import { Component, Input, Output, EventEmitter } from '@angular/core';
import { PaymentLinkService } from '../../../services/payment-link.service';
import { ToastService } from '../../../services/toast.service';
import { LoadingService } from '../../../services/loading.service';
import { ConfirmationConfig } from '../confirmation-modal/confirmation-modal.component';

@Component({
  selector: 'app-payment-link-manager',
  templateUrl: './payment-link-manager.component.html',
  styleUrls: ['./payment-link-manager.component.scss']
})
export class PaymentLinkManagerComponent {
  @Input() paymentLinkUrl: string = '';
  @Input() paymentLinkId: number | null = null;
  @Input() paymentLinkWompiId: string = '';
  @Input() isActive: boolean = true;
  @Input() showDeactivateButton: boolean = true;
  @Input() readonly: boolean = true;
  @Input() size: 'sm' | 'md' | 'lg' = 'md';
  
  @Output() paymentLinkDeactivated = new EventEmitter<void>();

  // Modal de confirmación
  showConfirmationModal = false;
  confirmationConfig: ConfirmationConfig = {
    title: '¿Desactivar link de pago?',
    message: '¿Está seguro de que desea desactivar este link de pago? Esta acción cambiará el estado de la cuota a pendiente y el link ya no será válido para realizar pagos.',
    confirmText: 'Sí, desactivar',
    cancelText: 'Cancelar',
    type: 'danger',
    icon: 'fa-ban',
    showIcon: true
  };

  constructor(
    private paymentLinkService: PaymentLinkService,
    private toastService: ToastService,
    private loadingService: LoadingService
  ) {}

  /**
   * Copia el link de pago al portapapeles
   */
  async copyPaymentLink(): Promise<void> {
    if (!this.paymentLinkUrl) {
      this.toastService.showToast('error', 'No hay link de pago para copiar');
      return;
    }

    try {
      const success = await this.paymentLinkService.copyToClipboard(this.paymentLinkUrl);
      if (success) {
        this.toastService.showToast('success', '✅ Link de pago copiado al portapapeles');
      } else {
        this.toastService.showToast('error', 'No se pudo copiar el link al portapapeles');
      }
    } catch (error) {
      console.error('Error al copiar link:', error);
      this.toastService.showToast('error', 'Error al copiar el link al portapapeles');
    }
  }

  /**
   * Desactiva el link de pago
   */
  async deactivatePaymentLink(): Promise<void> {
    // Verificar que tengamos al menos uno de los identificadores
    if (!this.paymentLinkId && !this.paymentLinkWompiId) {
      this.toastService.showToast('error', 'No se puede identificar el link de pago para desactivar');
      return;
    }

    // Mostrar modal de confirmación
    this.showConfirmationModal = true;
  }

  /**
   * Confirma la desactivación del link de pago
   */
  async onConfirmDeactivation(): Promise<void> {
    this.showConfirmationModal = false;
    try {
      this.loadingService.show();

      // Preferir usar el ID si está disponible, sino usar el WompiLinkId
      if (this.paymentLinkId) {
        await this.paymentLinkService.deactivatePaymentLink(this.paymentLinkId).toPromise();
      } else if (this.paymentLinkWompiId) {
        await this.paymentLinkService.deactivatePaymentLinkByWompiId(this.paymentLinkWompiId).toPromise();
      }

      this.toastService.showToast('success', '✅ Link de pago desactivado exitosamente');
      this.isActive = false;
      this.paymentLinkDeactivated.emit();

    } catch (error: any) {
      console.error('Error al desactivar payment link:', error);
      const errorMessage = error?.error?.errorMessage || error?.message || 'Error al desactivar el link de pago';
      this.toastService.showToast('error', errorMessage);
    } finally {
      this.loadingService.hide();
    }
  }

  /**
   * Cancela la desactivación del link de pago
   */
  onCancelDeactivation(): void {
    this.showConfirmationModal = false;
  }

  /**
   * Obtiene las clases CSS según el tamaño especificado
   */
  getInputClasses(): string {
    const baseClasses = 'w-full bg-gray-50 border border-gray-200 rounded-lg text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent';
    
    switch (this.size) {
      case 'sm':
        return `${baseClasses} px-3 py-2 text-sm`;
      case 'lg':
        return `${baseClasses} px-4 py-3 text-base`;
      default:
        return `${baseClasses} px-3 py-2.5 text-sm`;
    }
  }

  /**
   * Obtiene las clases CSS para los botones según el tamaño
   */
  getButtonClasses(): string {
    const baseClasses = 'inline-flex items-center justify-center border border-gray-300 rounded-lg font-medium transition-colors duration-200';
    
    switch (this.size) {
      case 'sm':
        return `${baseClasses} px-2 py-2 text-xs`;
      case 'lg':
        return `${baseClasses} px-4 py-3 text-sm`;
      default:
        return `${baseClasses} px-3 py-2.5 text-sm`;
    }
  }
}
